import { useMemo } from 'react'
import { faBuilding, faEnvelope, faPhone, faUser } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Stack, Typography } from '@mui/material'
import { CustomerType, SimpleCustomerLookupFragment } from 'src/api/api.generated'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { HiCIcon } from '../../icons/icon'
import HicChipForTag from '../../hic-chip/hic-chip-for-tag'

export function SimpleCustomerLookupOption(props: { option: SimpleCustomerLookupFragment; showEmail?: boolean; showPhoneNumber?: boolean; showTags?: boolean; showRelatedCompany?: boolean }) {
  const { option, showEmail, showPhoneNumber, showTags, showRelatedCompany } = props
  const formatDate = useFormatDate()
  const caption = useMemo(() => {
    if (option.type === CustomerType.PERSON) {
      const date = option.person?.birthDate ? formatDate(option.person.birthDate) : undefined
      const address = option.person?.birthAddress
      const isIta = address?.country?.isoCode === 'IT'
      const city = isIta && address?.municipality?.name ? address.municipality.name : address?.city
      if (date && city) {
        return `${date} (${city})`
      } else if (date && !city) {
        return date
      } else if (!date && city) {
        return `(${city})`
      } else {
        return undefined
      }
    } else {
      const address = option.address
      const street = address?.address
      const isIta = address?.country?.isoCode === 'IT'
      const city = isIta && address?.municipality?.name ? address.municipality.name : address?.city
      if (street && city) {
        return `${city} - ${street}`
      } else if (street && !city) {
        return street
      } else if (!street && city) {
        return city
      } else {
        return undefined
      }
    }
  }, [formatDate, props.option])
  return (
    <Stack direction="row" width="100%" alignItems="flex-start" spacing={1}>
      <FontAwesomeIcon icon={option.type === CustomerType.PERSON ? faUser : faBuilding} style={{ marginTop: '3px' }} />

      <Stack direction="column" width="100%">
        <Typography variant="body1">
          <strong>{option.name}</strong>
        </Typography>
        {caption && <Typography variant="caption">{caption}</Typography>}
        {showPhoneNumber && (option.mailContacts ?? []).length > 0 && (
          <Stack direction="row" spacing={0.5}>
            <HiCIcon>
              <FontAwesomeIcon icon={faEnvelope} />
            </HiCIcon>
            <Typography variant="caption">{option?.mailContacts?.[0].value}</Typography>
          </Stack>
        )}
        {showEmail && (option.phoneContacts ?? []).length > 0 && (
          <Stack direction="row" spacing={0.5}>
            <HiCIcon>
              <FontAwesomeIcon icon={faPhone} />
            </HiCIcon>
            <Typography variant="caption">{option?.phoneContacts?.[0].value}</Typography>
          </Stack>
        )}
        {showRelatedCompany && option.relatedCompany && option.relatedCompany.name.length > 0 && (
          <Stack direction="row" gap={0.5} width="100%" flexWrap="wrap">
            <HiCIcon>
              <FontAwesomeIcon icon={faBuilding} />
            </HiCIcon>
            <Typography variant="caption">{option.relatedCompany.name}</Typography>
          </Stack>
        )}
        {showTags && option.tags && option.tags.length > 0 && (
          <Stack direction="row" gap={0.5} width="100%" flexWrap="wrap">
            {option.tags.map((tag) => (
              <HicChipForTag key={tag.id} color={tag.color} label={tag.label} />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
